/* eslint-disable max-lines */
// PlansBo.tsx - Redesigned Plans List Page
import Button from '@components/atoms/ui-kit/button.tsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '@components/layout/page-header';
import { useTranslation } from 'react-i18next';
import Modal from '@components/atoms/modals/modal.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import {
  useSubscribePlan,
  BillingChannel
} from '@utils/api/back-office/plans-bo/use-sell-plan-bo';
import { InputActionMeta, MultiValue, SingleValue } from 'react-select';
import { LabelValueSelectInput } from '@utils/types/shared/label-value-select-input';
import { CustomerBo } from '@utils/types/back-office/customers';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../router/paths';
import { useGetCustomersToSellBo } from '@utils/api/back-office/customer-bo/use-get-customer-sell-bo';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { GridAction } from '@components/atoms/datatable/grid-action-controller';
import { useGetPlansBoEnhanced } from '@utils/api/back-office/plans-bo/use-get-plans-bo-enhanced';
import { useGetPlanBo } from '@utils/api/back-office/plans-bo/use-get-plan-bo';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  Plus,
  Eye,
  ShoppingCart,
  Copy,
  Mic,
  Radio,
  Languages,
  FileText,
  GraduationCap,
  GitCompare,
  Shield,
  Check,
  RefreshCw
} from 'lucide-react';
import cn from 'classnames';
import {
  FeatureName,
  PlanBoEnhanced
} from '@utils/types/back-office/update-plan-bo';
import CreatePlanWizard from '@components/back-office/plans/create-plan-wizard';
import { StatusBadge } from '@components/atoms/ui-kit/badge';

type Row = {
  id: number;
  sku: string;
  description: string;
  type: string;
  processing_type: string;
  features_summary: FeatureName[];
  active_subscriptions_count: number;
  stt_pricing_plan_name?: string | null;
  is_fair_use?: boolean;
  fair_use_rate_limit?: number | null;
};

// Feature icons mapping
const FEATURE_ICONS: Record<FeatureName, typeof Mic> = {
  batch: Mic,
  realtime: Radio,
  alignment: GitCompare,
  translation: Languages,
  templates: FileText,
  training: GraduationCap
};

export default function PlansBo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();

  const [selectedPlan, setSelectedPlan] = useState<string | undefined>();
  const [sellPlan, setSellPlan] = useState(false);
  const [isSelling, setIsSelling] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [duplicatePlanId, setDuplicatePlanId] = useState<number | undefined>();

  // Use enhanced API for richer data
  const { data: plans, isLoading: plansLoading } = useGetPlansBoEnhanced();
  const { data: planBo, isLoading: planBoLoading } = useGetPlanBo(selectedPlan);
  const { mutate: subscribePlan } = useSubscribePlan();

  const [customerSearch, setCustomerSearch] = useState('');
  const { data: customers } = useGetCustomersToSellBo(customerSearch);

  const [selectedCustomer, setSelectedCustomer] =
    useState<SingleValue<LabelValueSelectInput>>(null);
  const [isAutoRenewChecked, setAutoRenew] = useState(true);
  const [billingChannel, setBillingChannel] =
    useState<BillingChannel>('SCRIPTIX');

  const customerOptions: LabelValueSelectInput[] =
    customers?.result?.map((c: CustomerBo) => ({
      label: `${c.name}  (#${c.id})`,
      value: String(c.id)
    })) || [];

  const handleCustomerChange = (
    selectedOption:
      | SingleValue<LabelValueSelectInput>
      | MultiValue<LabelValueSelectInput>
  ) => {
    setSelectedCustomer(selectedOption as SingleValue<LabelValueSelectInput>);
  };

  const handleSell = () => {
    if (!selectedCustomer?.value) return;
    if (!planBo?.result?.id) {
      toast.error(t('an-error-occurred-while-subscribing-to-the-plan'));
      return;
    }
    setIsSelling(true);
    subscribePlan(
      {
        id: planBo.result.id.toString(),
        payload: {
          customer_id: parseInt(selectedCustomer.value.toString(), 10),
          auto_renew: isAutoRenewChecked,
          // Only send billing_channel if it's ON_PREM (SCRIPTIX is the default)
          ...(billingChannel === 'ON_PREM' && { billing_channel: 'ON_PREM' })
        }
      },
      {
        onSuccess: () => {
          toast.success(t('subscription-successfully-completed'));
          setIsSelling(false);
          setSellPlan(false);
          setSelectedCustomer(null);
        },
        onError: (error: AxiosError<BaseError>) => {
          const errorMessage =
            error.response?.data?.error_description ||
            error.response?.data?.detail ||
            error.response?.data?.error ||
            error.response?.data?.message ||
            t('an-error-occurred-while-subscribing-to-the-plan');

          toast.error(errorMessage);
          setIsSelling(false);
        }
      }
    );
  };

  const rows = useMemo<Row[]>(
    () =>
      (plans?.result ?? [])
        .filter(
          (p): p is PlanBoEnhanced & { id: number } => typeof p.id === 'number'
        )
        .map(p => ({
          id: p.id,
          type: p.type || '',
          sku: p.sku,
          processing_type: p.processing_type,
          description: p.description,
          features_summary: p.features_summary || [],
          active_subscriptions_count: p.active_subscriptions_count || 0,
          stt_pricing_plan_name: p.stt_pricing_plan_name,
          is_fair_use: p.is_fair_use,
          fair_use_rate_limit: p.fair_use_rate_limit
        })),
    [plans]
  );

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        header: t('id'),
        field: 'id',
        maxWidth: 80,
        minWidth: 70,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        filterKey: 'id',
        serverOperator: 'eq',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('sku'),
        field: 'sku',
        minWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'sku',
        cell: ({ getValue }) => (
          <span className="font-medium">{String(getValue() ?? '')}</span>
        )
      },
      {
        header: t('description'),
        field: 'description',
        filterKey: 'description',
        filterVariant: 'text',
        minWidth: 200,
        enableSorting: false,
        enableFiltering: true,
        cell: ({ getValue }) => (
          <span
            className="text-sm truncate block max-w-[200px]"
            title={String(getValue() ?? '')}
          >
            {String(getValue() ?? '')}
          </span>
        )
      },
      {
        header: t('type'),
        field: 'type',
        minWidth: 130,
        maxWidth: 150,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'type',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: 'Bundle', value: 'Bundle' },
          { label: 'Pay as you Go', value: 'Pay as you Go' },
          { label: 'Perpetual', value: 'Perpetual' },
          { label: 'Other', value: 'Other' }
        ],
        cell: ({ getValue }) => {
          const type = String(getValue() ?? 'Other');
          return <StatusBadge status={type} />;
        }
      },
      {
        header: t('limits'),
        field: 'is_fair_use',
        minWidth: 100,
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) => {
          const isFairUse = row.original.is_fair_use;
          const rateLimit = row.original.fair_use_rate_limit;
          if (isFairUse) {
            return (
              <div
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/15 text-green-700 dark:bg-green-500/10 dark:text-green-400"
                title={
                  rateLimit
                    ? `${t('fair-use')} - ${rateLimit} ${t('requests-per-minute')}`
                    : t('fair-use')
                }
              >
                <Shield className="h-3 w-3" />
                <span>{t('fair-use')}</span>
              </div>
            );
          }
          return (
            <span
              style={{ color: getColour('inactiveText') }}
              className="text-xs"
            >
              {t('hard-limit')}
            </span>
          );
        }
      },
      {
        header: t('features'),
        field: 'features_summary',
        filterKey: 'feature',
        filterVariant: 'select',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('batch'), value: 'batch' },
          { label: t('realtime'), value: 'realtime' },
          { label: t('alignment'), value: 'alignment' },
          { label: t('translation'), value: 'translation' },
          { label: t('training'), value: 'training' },
          { label: t('templates'), value: 'templates' }
        ],
        minWidth: 220,
        enableSorting: false,
        enableFiltering: true,
        cell: ({ getValue }) => {
          const features = getValue() as FeatureName[];
          if (!features || features.length === 0) {
            return <span style={{ color: getColour('inactiveText') }}>—</span>;
          }
          return (
            <div className="flex flex-wrap items-center gap-1">
              {features.slice(0, 3).map(feature => {
                const Icon = FEATURE_ICONS[feature];
                return (
                  <div
                    key={feature}
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded-2xl text-xs"
                    style={{ backgroundColor: getColour('thirdLayer') }}
                  >
                    <Icon
                      className="h-3 w-3"
                      style={{ color: getColour('activeText') }}
                    />
                    <span style={{ color: getColour('normalText') }}>
                      {t(feature)}
                    </span>
                  </div>
                );
              })}
              {features.length > 3 && (
                <span
                  className="text-xs px-1.5"
                  style={{ color: getColour('inactiveText') }}
                >
                  +{features.length - 3}
                </span>
              )}
            </div>
          );
        }
      },
      {
        header: t('subscriptions'),
        field: 'active_subscriptions_count',
        minWidth: 120,
        maxWidth: 130,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const count = Number(getValue() ?? 0);
          return (
            <span
              className={cn(
                'px-2 py-0.5 rounded-2xl text-xs font-medium',
                count > 0
                  ? 'bg-blue-500/15 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400'
                  : ''
              )}
              style={
                count === 0 ? { color: getColour('inactiveText') } : undefined
              }
            >
              {count}
            </span>
          );
        }
      },
      {
        header: t('processing-type'),
        field: 'processing_type',
        minWidth: 130,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'processing_type',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('batch'), value: 'BATCH' },
          { label: t('real-time'), value: 'REALTIME' },
          { label: t('other'), value: 'OTHER' }
        ],
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ],
    [t, getColour]
  );

  const handleCellClick = (row: Row) => {
    if (!row?.id) return;
    navigate(PATHS(String(row.id)).PLAN_DETAILS);
  };

  // Action buttons for mobile & desktop
  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'view',
        label: t('view'),
        icon: <Eye className="h-4 w-4" />,
        intent: 'primary',
        run: (row: Row) => {
          navigate(PATHS(String(row.id)).PLAN_DETAILS);
        }
      },
      {
        id: 'sell',
        label: t('sell'),
        icon: <ShoppingCart className="h-4 w-4" />,
        run: (row: Row) => {
          setSelectedPlan(String(row.id));
          setSellPlan(true);
        }
      },
      {
        id: 'duplicate',
        label: t('duplicate'),
        icon: <Copy className="h-4 w-4" />,
        run: (row: Row) => {
          setDuplicatePlanId(row.id);
          setIsCreateModalOpen(true);
        }
      }
    ],
    [t, navigate]
  );

  // debounced customer search
  const debouncedSearch = useMemo(
    () =>
      debounce((v: string) => {
        const q = v.trim();
        setCustomerSearch(q.length >= 2 ? q : '');
      }, 300),
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleInputChange = useCallback(
    (value: string, meta?: InputActionMeta) => {
      if (!meta || meta.action === 'input-change') {
        debouncedSearch(value);
      }
      return value;
    },
    [debouncedSearch]
  );

  const SELL_FORM_ID = 'sell-plan-form';

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col">
      <PageHeader title={t('plans')} caption={t('view-and-update-plans-below')}>
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          label={t('new-plan')}
          icon={<Plus width={2} className="h-5 w-5" />}
        />
      </PageHeader>

      <CreatePlanWizard
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setDuplicatePlanId(undefined);
        }}
        duplicateFromPlanId={duplicatePlanId}
      />

      <div className="w-full p-2 md:p-0 mt-4 flex-1 min-h-0 flex flex-col">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          totalItems={plans?.total_results ?? rows.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          fill
          dense
          grid={false}
          striped
          loading={plansLoading || !plans}
          getRowId={row => String(row.id)}
          onRowClick={handleCellClick}
        />

        {sellPlan && (
          <Modal
            isOpen={sellPlan}
            onClose={() => {
              setSellPlan(false);
              setSelectedCustomer(null);
              setBillingChannel('SCRIPTIX');
            }}
            size="small"
            title={t('sell-plan')}
            caption={t('assign-plan-to-customer')}
            footerActions={[
              <Button
                onClick={() => {
                  setSellPlan(false);
                  setSelectedCustomer(null);
                  setBillingChannel('SCRIPTIX');
                }}
                label={t('cancel')}
                size="small"
                variant="smoke"
                key="cancel"
              />,
              <Button
                label={isSelling ? t('selling') : t('sell')}
                size="small"
                variant="primary"
                type="submit"
                form={SELL_FORM_ID}
                key="sell"
                disabled={!selectedCustomer || isSelling || planBoLoading}
                loading={planBoLoading}
              />
            ]}
          >
            <form
              id={SELL_FORM_ID}
              onSubmit={e => {
                e.preventDefault();
                handleSell();
              }}
              className="space-y-4"
            >
              <button type="submit" className="hidden" aria-hidden="true" />

              {/* Plan Info Card */}
              {planBo?.result && (
                <div
                  className="p-3 rounded-2xl border"
                  style={{
                    backgroundColor: getColour('secondLayer'),
                    borderColor: getColour('border')
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-xs font-medium"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('plan')}
                    </span>
                    {planBo.result.type && (
                      <StatusBadge status={String(planBo.result.type)} />
                    )}
                  </div>
                  <p
                    className="text-sm font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {planBo.result.sku}
                  </p>
                  {planBo.result.description && (
                    <p
                      className="text-xs mt-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {planBo.result.description}
                    </p>
                  )}
                </div>
              )}

              {/* Customer Selection */}
              <div>
                <label
                  className="block text-sm font-medium mb-1.5"
                  style={{ color: getColour('normalText') }}
                >
                  {t('customer')} <span className="text-red-500">*</span>
                </label>
                <SelectInput
                  options={customerOptions}
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  onInputChange={handleInputChange}
                  placeholder={t('search-customers')}
                  menuPlacement="bottom"
                  styles={{
                    menu: base => ({ ...base, zIndex: 10000 }),
                    menuPortal: base => ({ ...base, zIndex: 10000 })
                  }}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                <p
                  className="text-xs mt-1"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('search-and-select-customer-to-assign-plan')}
                </p>
              </div>

              {/* Auto-renew Toggle */}
              <div>
                <label
                  className="block text-sm font-medium mb-1.5"
                  style={{ color: getColour('normalText') }}
                >
                  {t('subscription-options')}
                </label>
                <button
                  type="button"
                  onClick={() => setAutoRenew(!isAutoRenewChecked)}
                  className="relative w-full p-3 rounded-2xl border-2 text-left transition-all"
                  style={{
                    borderColor: isAutoRenewChecked
                      ? 'rgba(20, 184, 166, 0.7)'
                      : getColour('border'),
                    backgroundColor: isAutoRenewChecked
                      ? 'rgba(20, 184, 166, 0.08)'
                      : getColour('secondLayer')
                  }}
                >
                  {isAutoRenewChecked && (
                    <div
                      className="absolute top-2 right-2 h-4 w-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(20, 184, 166, 0.9)' }}
                    >
                      <Check className="h-2.5 w-2.5 text-white" />
                    </div>
                  )}

                  <div
                    className="h-7 w-7 rounded-2xl flex items-center justify-center mb-2"
                    style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}
                  >
                    <RefreshCw
                      className="h-3.5 w-3.5"
                      style={{ color: 'rgba(20, 184, 166, 0.9)' }}
                    />
                  </div>
                  <span
                    className="text-xs font-medium"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('auto-renew')}
                  </span>
                  <span
                    className="text-[10px] block mt-0.5"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('auto-renew-description')}
                  </span>
                </button>
              </div>

              {/* Billing Channel */}
              <div>
                <label
                  className="block text-sm font-medium mb-1.5"
                  style={{ color: getColour('normalText') }}
                >
                  {t('billing-channel')}
                </label>
                <SelectInput
                  options={[
                    {
                      value: 'SCRIPTIX',
                      label: t('scriptix-billing')
                    },
                    {
                      value: 'ON_PREM',
                      label: t('on-premise-billing')
                    }
                  ]}
                  value={{
                    value: billingChannel,
                    label:
                      billingChannel === 'SCRIPTIX'
                        ? t('scriptix-billing')
                        : t('on-premise-billing')
                  }}
                  onChange={opt =>
                    setBillingChannel(
                      (opt?.value as BillingChannel) || 'SCRIPTIX'
                    )
                  }
                  menuPlacement="top"
                  styles={{
                    menu: base => ({ ...base, zIndex: 10000 }),
                    menuPortal: base => ({ ...base, zIndex: 10000 })
                  }}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                <p
                  className="text-xs mt-1"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('billing-channel-description')}
                </p>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </div>
  );
}
